import { useSyncExternalStore } from 'react';
import type { VariboxStatusKey } from '@/data/variboxMockData';
import type { MovementLocationCode, VariboxPlacement } from '@/data/movementLocationConfig';

export type AvailabilityKey = 'enabled' | 'disabled';

export interface MovementPhoto {
  fileName: string;
  dataUrl: string;
}

export interface MovementRecord {
  id: string;
  variboxSerial: string;
  variboxRadexId: string;
  previousPlacement: VariboxPlacement;
  destinationPlacement: {
    locationCode: MovementLocationCode;
    optionId: string;
  };
  happenedAtIso: string;
  user: string;
  status: VariboxStatusKey;
  availability: AvailabilityKey;
  note?: string;
  photo?: MovementPhoto;
}

interface RecordMovementInput {
  variboxSerial: string;
  variboxRadexId: string;
  previousPlacement: VariboxPlacement;
  destinationPlacement: {
    locationCode: MovementLocationCode;
    optionId: string;
  };
  user: string;
  status: VariboxStatusKey;
  availability: AvailabilityKey;
  note?: string;
  photo?: MovementPhoto;
}

interface MovementStoreState {
  movementHistory: MovementRecord[];
  locationOverrides: Record<string, VariboxPlacement>;
}

const movementStoreState: MovementStoreState = {
  movementHistory: [],
  locationOverrides: {},
};

const listeners = new Set<() => void>();

function notifyListeners() {
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot() {
  return movementStoreState;
}

function appendMovement(input: RecordMovementInput) {
  const movement: MovementRecord = {
    id: `${input.variboxSerial}-${Date.now()}`,
    variboxSerial: input.variboxSerial,
    variboxRadexId: input.variboxRadexId,
    previousPlacement: input.previousPlacement,
    destinationPlacement: input.destinationPlacement,
    happenedAtIso: new Date().toISOString(),
    user: input.user,
    status: input.status,
    availability: input.availability,
    note: input.note,
    photo: input.photo,
  };

  movementStoreState.movementHistory = [movement, ...movementStoreState.movementHistory];
  movementStoreState.locationOverrides = {
    ...movementStoreState.locationOverrides,
    [input.variboxSerial]: {
      locationCode: input.destinationPlacement.locationCode,
      optionId: input.destinationPlacement.optionId,
    },
  };

  notifyListeners();

  return movement;
}

export function useMovementDemoState() {
  const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  return {
    movementHistory: state.movementHistory,
    locationOverrides: state.locationOverrides,
    recordMovement: appendMovement,
  };
}
